import { Table } from '@tanstack/react-table';
import clsx from 'clsx';
import {
  FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight
} from 'react-icons/fi';

import styles from './pagination.module.scss';


interface PaginationProps<T> {
  table: Table<T>;
}

export function Pagination<T>( { table }: PaginationProps<T> ) {
  const pageCount = table.getPageCount();

  if ( pageCount === 0 ) {
    return null;
  }

  const { pageIndex, pageSize } = table.getState().pagination;
  // Use getRowCount() for server-side pagination, fall back to client-side row count
  const totalResults = table.getRowCount() > 0 ? table.getRowCount() : table.getFilteredRowModel().rows.length;
  const startResult = pageIndex * pageSize + 1;
  const endResult = Math.min( ( pageIndex + 1 ) * pageSize, totalResults );

  return (
    <div className={ clsx( 'layout__row', 'pad' ) }>
      <div className={ styles.pagination }>
        <div className={ styles.pagination__controls }>
          <button
            onClick={ () => table.setPageIndex( 0 ) }
            disabled={ !table.getCanPreviousPage() }
            className={ styles.pagination__button }
            title="First page"
            aria-label="First page"
          >
            <FiChevronsLeft />
          </button>
          <button
            onClick={ () => table.previousPage() }
            disabled={ !table.getCanPreviousPage() }
            className={ styles.pagination__button }
            title="Previous page"
            aria-label="Previous page"
          >
            <FiChevronLeft />
          </button>
          <span className={ styles.pagination__info }>
            Page{' '}
            <select
              value={ pageIndex + 1 }
              onChange={ ( e ) => {
                const target = Number( e.target.value );
                if ( !Number.isNaN( target ) ) {
                  table.setPageIndex( target - 1 );
                }
              } }
              className={ styles.pagination__select }
              title={ `Go to page (1-${ pageCount })` }
              aria-label={ `Go to page, current page ${ pageIndex + 1 } of ${ pageCount }` }
              disabled={ pageCount <= 1 }
            >
              { Array.from( { length: pageCount }, ( _, i ) => i + 1 ).map( ( n ) => (
                <option key={ n } value={ n }>
                  { n }
                </option>
              ) ) }
            </select>
            {' '}of { pageCount }
          </span>
          <button
            onClick={ () => table.nextPage() }
            disabled={ !table.getCanNextPage() }
            className={ styles.pagination__button }
            title="Next page"
            aria-label="Next page"
          >
            <FiChevronRight />
          </button>
          <button
            onClick={ () => table.setPageIndex( pageCount - 1 ) }
            disabled={ !table.getCanNextPage() }
            className={ styles.pagination__button }
            title="Last page"
            aria-label="Last page"
          >
            <FiChevronsRight />
          </button>
        </div>

        <div className={ styles.pagination__info }>
          <span>
            Showing {startResult} to {endResult} of {totalResults} results
          </span>
          <select
            value={ pageSize }
            onChange={ e => table.setPageSize( Number( e.target.value ) ) }
            className={ styles.pagination__select }
          >
            { [ 10, 20, 30, 50, 100 ].map( pageSize => (
              <option key={ pageSize } value={ pageSize }>
                Show { pageSize }
              </option>
            ) ) }
          </select>
        </div>
      </div>
    </div>
  );
}